import React, { useState, useEffect, useMemo } from "react";
import { Button, DataTable, clientSortRows } from "./ui";
import { useNavigate } from "react-router-dom";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBInput, MDBDropdown, MDBDropdownToggle, MDBDropdownMenu, MDBDropdownItem } from "mdb-react-ui-kit";
import { api } from "../services";
import { getImageUrl } from "../utils/utils";
import { useTranslation } from "react-i18next";

function ProductListing() {
  const { t } = useTranslation("translation");
  const [viewMode, setViewMode] = useState("list");
  const [productsData, setProductsData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("Select Branch");
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [storeId, setStoreId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableSortColumn, setTableSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const navigate = useNavigate();

  useEffect(() => {
    fetchStoreId(); // Fetch store ID on mount
  }, []);

  useEffect(() => {
    if (storeId) {
      fetchBranches(); // Fetch branches after store ID is fetched
      fetchProducts(); // Fetch all products initially
    }
  }, [storeId]);

  useEffect(() => {
    if (selectedBranchId) {
      fetchProducts(selectedBranchId); // Fetch products for the selected branch
    }
  }, [selectedBranchId]);

  // Fetch Store ID
  const fetchStoreId = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.stores && response.data.stores.length > 0) {
        setStoreId(response.data.stores[0]._id);
      }
    } catch (error) {
      console.error(
        "Error fetching store ID:",
        error.response?.data || error.message
      );
    }
  };

  // Fetch Branches for the Selected Store
  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${storeId}/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error(
        "Error fetching branches:",
        error.response?.data || error.message
      );
    }
  };

  // Fetch Products (All or By Branch)
  const fetchProducts = async (branchId = null) => {
    try {
      const token = localStorage.getItem("token");
      let endpoint = branchId
        ? `/branches/${branchId}/products`
        : `/stores/${storeId}/products`;

      const response = await api.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setProductsData(response.data.products || []);
    } catch (error) {
      console.error(
        "Error fetching products:",
        error.response?.data || error.message
      );
    }
  };

  // Handle Branch Selection
  const handleBranchSelect = (branch) => {
    setSelectedBranch(branch.name);
    setSelectedBranchId(branch._id);
    fetchProducts(branch._id);
  };

  const handleViewModeChange = (mode) => setViewMode(mode);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const handleAddProductClick = () => navigate("/add-product");

  const handleEditProductClick = (product) => {
    navigate(`/edit-product/${product._id}`);
  };

  const filteredProducts = productsData.filter((product) =>
    product.name.en.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedProducts = useMemo(
    () =>
      clientSortRows(filteredProducts, tableSortColumn, sortDirection, (product, col) => {
        switch (col) {
          case "name":
            return (product.name?.en || "").toLowerCase();
          case "price":
            return product.price ?? 0;
          case "calories":
            return product.calories ?? 0;
          default:
            return "";
        }
      }),
    [filteredProducts, tableSortColumn, sortDirection]
  );

  const columns = useMemo(
    () => [
      {
        id: "image",
        header: "Product Image",
        render: (product) => (
          <img
            src={getImageUrl(product.image)}
            alt={product.name.en}
            className="img-fluid"
            style={{ maxHeight: "50px", objectFit: "cover" }}
          />
        ),
      },
      {
        id: "name",
        header: "Product Name",
        sortable: true,
        strong: true,
        render: (product) => product.name.en,
      },
      {
        id: "price",
        header: "Price",
        sortable: true,
        render: (product) => `$${product.price}`,
      },
      {
        id: "calories",
        header: "Calories",
        sortable: true,
        render: (product) => product.calories,
      },
    ],
    []
  );

  return (
    <MDBContainer fluid className="p-4 content-area flex-grow-1">
      <h2 className="mb-4">Product Listing</h2>

      {/* Branch Selection */}
      <MDBRow className="mb-4">
        <MDBCol md="6">
          <label>Select Branch</label>
          <MDBDropdown>
            <MDBDropdownToggle color="light">
              {selectedBranch}
            </MDBDropdownToggle>
            <MDBDropdownMenu>
              {branches.map((branch) => (
                <MDBDropdownItem
                  key={branch._id}
                  onClick={() => handleBranchSelect(branch)}
                >
                  {branch.name}
                </MDBDropdownItem>
              ))}
            </MDBDropdownMenu>
          </MDBDropdown>
        </MDBCol>
      </MDBRow>

      {/* Quick Actions */}
      <MDBRow className="mb-4">
        <h6>Quick Actions</h6>
        <MDBCol>
          <Button
            color="primary"
            className="quick-action-btn"
            onClick={handleAddProductClick}
          >
            Add New Product
          </Button>
        </MDBCol>
      </MDBRow>

      {/* Search and View Toggle */}
      <MDBRow className="mb-4 d-flex align-items-center">
        <MDBCol md="6">
          <MDBInput
            type="text"
            placeholder="Search Products"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </MDBCol>
        <MDBCol
          md="6"
          className="d-flex justify-content-end align-items-center"
        >
          <div className="ms-3">
            <Button
              color="light"
              onClick={() => handleViewModeChange("list")}
              active={viewMode === "list"}
            >
              List
            </Button>
            <Button
              color="light"
              onClick={() => handleViewModeChange("grid")}
              active={viewMode === "grid"}
            >
              Grid
            </Button>
          </div>
        </MDBCol>
      </MDBRow>

      {/* Product Content */}
      {viewMode === "grid" ? (
        <MDBRow>
          {filteredProducts.map((product) => (
            <MDBCol md="4" key={product._id}>
              <MDBCard className="order-card h-100">
                <MDBCardBody className="position-relative text-center">
                  <img
                    src={product.imagePath || "https://via.placeholder.com/150"}
                    alt={product.name.en}
                    className="img-fluid mb-3"
                    style={{ maxHeight: "150px", objectFit: "cover" }}
                  />
                  <h5 className="mb-1">{product.name.en}</h5>
                  <small className="text-muted">Price: ${product.price}</small>
                  <div className="text-center my-3">
                    <h6>Calories: {product.calories}</h6>
                  </div>
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => handleEditProductClick(product)}
                  >
                    Edit
                  </Button>
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
          ))}
        </MDBRow>
      ) : (
        <DataTable
          tableId="product-listing"
          title={t("products")}
          selectionMode="index"
          columns={columns}
          data={sortedProducts}
          rowKey="_id"
          emptyState="No products to display"
          onRowClick={handleEditProductClick}
          sort={{
            column: tableSortColumn,
            direction: sortDirection,
            onChange: ({ column, direction }) => {
              setTableSortColumn(column);
              setSortDirection(direction);
            },
          }}
        />
      )}
    </MDBContainer>
  );
}

export default ProductListing;
